/**
 * Fiscal Printer - KONTRAKT 1.0: przegladarka jest KABLEM, nie kalkulatorem.
 *
 * Ten modul robi dokladnie dwie rzeczy:
 *   1) sprawdza, czy kontrakt z serwera jest kompletny i w znanej wersji,
 *   2) sklada z GOTOWYCH CIAGOW pakiety XML w kolejnosci wymaganej przez Novitusa.
 *
 * Czego tu NIE MA i byc nie moze (to sa dokladnie te miejsca, ktore zrodzily
 * bledy 1-3 z rozpoznania):
 *   - parseFloat / toFixed / Math.round na czymkolwiek, co idzie do pakietu,
 *   - rozdzielania rabatu, przycinania go do wartosci linii, mapowania stawek VAT,
 *   - skladania nazwy fiskalnej, prefiksu, skracania do 40 znakow,
 *   - sortowania, filtrowania, scalania ani pomijania pozycji,
 *   - wartosci domyslnych typu (|| 'Produkt'), (|| 'Rabat'), (|| 23), (|| 'szt') -
 *     brak pola to BLAD KONTRAKTU, a nie okazja do zgadywania.
 *
 * Szablony pakietow sa przepisane ZNAK W ZNAK z receiptXML.js (sciezka stara).
 * Zmienia sie wylacznie zrodlo wartosci: kontrakt zamiast obliczen w JS.
 */

// Wersje kontraktu, ktore ta przegladarka umie zlozyc. Wysylane do serwera
// jako `akceptowane_wersje` - serwer wybiera najwyzsza wspolna.
export const AKCEPTOWANE_WERSJE = ['1.0'];

// MAJOR-y, ktore umiemy zinterpretowac. Nowy MINOR (1.1) to tylko dodane pola -
// ignorujemy nieznane i drukujemy dalej. Nowy MAJOR = odmowa druku.
const OBSLUGIWANE_MAJOR = new Set(AKCEPTOWANE_WERSJE.map(w => String(w).split('.')[0]));

const PROTOKOL_OBSLUGIWANY = 'xml';

// Opoznienia miedzy pakietami - BEZ ZMIAN wzgledem dzisiejszego kodu.
const CZEKAJ_PO_CANCEL = 150;
const CZEKAJ_PO_BEGIN = 150;
const CZEKAJ_PO_POZYCJI = 150;
const CZEKAJ_PO_POZYCJI_Z_RABATEM = 300;
const CZEKAJ_PO_KAUCJI = 300;
const CZEKAJ_PO_PLATNOSCI = 300;

// Okno odczytu dla <receipt action="close"> - druk fizyczny trwa kilka sekund.
const OPCJE_CLOSE = { responseTimeout: 6000, idleGap: 250 };

function jestTekstem(x) {
  return typeof x === 'string';
}

function blad(kod, opis) {
  return { ok: false, kod, blad: opis };
}

/**
 * Sprawdza kontrakt PRZED wyslaniem czegokolwiek do drukarki.
 * Zwraca { ok: true } albo { ok: false, kod, blad }.
 *
 * Kazda odmowa oznacza: NIE DRUKUJEMY. Nie ma sciezki "w razie watpliwosci
 * sprobuj po swojemu" - to bylby powrot do liczenia w przegladarce.
 */
export function sprawdzKontrakt(kontrakt) {
  if (!kontrakt || typeof kontrakt !== 'object') {
    return blad('BRAK_KONTRAKTU', 'Serwer nie zwrocil paragonu do wydruku');
  }

  const wersja = kontrakt.wersja;
  if (!jestTekstem(wersja) || !wersja) {
    return blad('WERSJA_NIEZNANA', 'Paragon z serwera nie ma numeru wersji kontraktu');
  }
  const major = wersja.split('.')[0];
  if (!OBSLUGIWANE_MAJOR.has(major)) {
    return blad('WERSJA_NIEZNANA',
      `Serwer wydal paragon w wersji ${wersja}, ktorej ta kasa nie zna. Zaktualizuj aplikacje kasy.`);
  }

  if (kontrakt.protokol !== PROTOKOL_OBSLUGIWANY) {
    return blad('PROTOKOL_NIEZNANY',
      `Nieznany protokol drukarki w kontrakcie: ${kontrakt.protokol}`);
  }

  const naglowek = kontrakt.naglowek;
  if (!naglowek || typeof naglowek !== 'object') {
    return blad('BRAK_POLA', 'Kontrakt bez naglowka paragonu');
  }
  for (const pole of ['tryb', 'kasa', 'numer_systemowy', 'kasjer', 'nip_nabywcy']) {
    if (!jestTekstem(naglowek[pole])) {
      return blad('BRAK_POLA', `Kontrakt: brak pola naglowek.${pole}`);
    }
  }

  const pozycje = kontrakt.pozycje;
  if (!Array.isArray(pozycje) || pozycje.length === 0) {
    return blad('BRAK_POZYCJI', 'Kontrakt nie zawiera zadnej pozycji paragonu');
  }
  for (let i = 0; i < pozycje.length; i++) {
    const p = pozycje[i];
    if (!p || typeof p !== 'object') {
      return blad('BRAK_POLA', `Kontrakt: pozycja ${i + 1} nie jest obiektem`);
    }
    for (const pole of ['nazwa', 'ilosc', 'jednostka', 'ptu', 'cena']) {
      if (!jestTekstem(p[pole]) || p[pole] === '') {
        return blad('BRAK_POLA', `Kontrakt: pozycja ${i + 1} bez pola ${pole}`);
      }
    }
    // rabat === null znaczy: NIE wstawiaj znacznika <discount> w ogole.
    if (p.rabat !== null && p.rabat !== undefined) {
      if (typeof p.rabat !== 'object') {
        return blad('BRAK_POLA', `Kontrakt: pozycja ${i + 1} ma niepoprawny rabat`);
      }
      for (const pole of ['nazwa', 'kwota', 'akcja']) {
        if (!jestTekstem(p.rabat[pole])) {
          return blad('BRAK_POLA', `Kontrakt: pozycja ${i + 1}, rabat bez pola ${pole}`);
        }
      }
    }
  }

  const kaucje = kontrakt.kaucje;
  if (!Array.isArray(kaucje)) {
    return blad('BRAK_POLA', 'Kontrakt: kaucje musza byc lista (moze byc pusta)');
  }
  for (let i = 0; i < kaucje.length; i++) {
    const k = kaucje[i];
    if (!k || typeof k !== 'object') {
      return blad('BRAK_POLA', `Kontrakt: kaucja ${i + 1} nie jest obiektem`);
    }
    for (const pole of ['akcja', 'kierunek', 'cena', 'ilosc']) {
      if (!jestTekstem(k[pole]) || k[pole] === '') {
        return blad('BRAK_POLA', `Kontrakt: kaucja ${i + 1} bez pola ${pole}`);
      }
    }
  }

  const platnosci = kontrakt.platnosci;
  // ZAWSZE lista, takze przy jednej formie platnosci - dzieki temu z przegladarki
  // znika rozgalezienie if (payments.length > 0) ... else ... (receiptXML.js:565).
  if (!Array.isArray(platnosci) || platnosci.length === 0) {
    return blad('BRAK_POLA', 'Kontrakt nie zawiera zadnej platnosci');
  }
  for (let i = 0; i < platnosci.length; i++) {
    const z = platnosci[i];
    if (!z || typeof z !== 'object') {
      return blad('BRAK_POLA', `Kontrakt: platnosc ${i + 1} nie jest obiektem`);
    }
    for (const pole of ['typ', 'nazwa', 'kwota']) {
      if (!jestTekstem(z[pole]) || z[pole] === '') {
        return blad('BRAK_POLA', `Kontrakt: platnosc ${i + 1} bez pola ${pole}`);
      }
    }
  }

  const sumy = kontrakt.sumy;
  if (!sumy || !jestTekstem(sumy.suma_fiskalna) || sumy.suma_fiskalna === '') {
    return blad('BRAK_POLA', 'Kontrakt: brak sumy fiskalnej paragonu');
  }

  // Liczniki kontrolne. Rozjazd znaczy, ze cos po drodze zgubilo pozycje -
  // wtedy NIE drukujemy, bo paragon bylby niepelny.
  const kontrola = kontrakt.kontrola;
  if (kontrola && typeof kontrola === 'object') {
    const pary = [
      ['pozycji', pozycje.length],
      ['kaucji', kaucje.length],
      ['platnosci', platnosci.length],
    ];
    for (const [pole, ile] of pary) {
      const oczekiwane = kontrola[pole];
      if (typeof oczekiwane === 'number' && oczekiwane !== ile) {
        return blad('NIEZGODNE_LICZNIKI',
          `Kontrakt niespojny: serwer zapowiedzial ${oczekiwane} (${pole}), przyszlo ${ile}`);
      }
    }
  }

  return { ok: true };
}

/**
 * Sklada liste pakietow XML z gotowego kontraktu.
 *
 * Zwraca tablice krokow: { krok, xml, czekajMs, czytaj, ignorujBlad?, opcje?, lp? }.
 * Nadawca (receiptXML.js) tylko je wysyla - nie modyfikuje tresci.
 *
 * Kolejnosc (bez zmian wzgledem dzisiaj):
 *   cancel -> begin -> item[] -> container[] -> payment[] -> close
 */
export function zbudujPakiety(kontrakt) {
  const naglowek = kontrakt.naglowek;
  const kroki = [];

  // Krok 0: anulowanie ewentualnej otwartej transakcji. Bledy ignorowane.
  kroki.push({
    krok: 'cancel',
    xml: '<packet><receipt action="cancel"></receipt></packet>',
    czekajMs: CZEKAJ_PO_CANCEL,
    czytaj: false,
    ignorujBlad: true,
  });

  // Krok 1: otwarcie paragonu.
  kroki.push({
    krok: 'begin',
    xml: `<packet><receipt action="begin" mode="${naglowek.tryb}"></receipt></packet>`,
    czekajMs: CZEKAJ_PO_BEGIN,
    czytaj: true,
  });

  // Krok 2: pozycje. Rabat MUSI byc zagniezdzony w <item>...</item> w jednym
  // pakiecie (Novitus XML sekcja 12) - standalone <discount> drukarka liczy
  // jako rabat od sumy paragonu.
  for (const poz of kontrakt.pozycje) {
    const rabat = poz.rabat;
    const fragmentRabatu = rabat
      ? `<discount name="${rabat.nazwa}" value="${rabat.kwota}" action="${rabat.akcja}"></discount>`
      : '';
    kroki.push({
      krok: 'item',
      lp: poz.lp,
      xml: `<packet><item name="${poz.nazwa}" quantity="${poz.ilosc}" quantityunit="${poz.jednostka}" ptu="${poz.ptu}" price="${poz.cena}" recipe="" charge="" plu="" description="" action="sale">${fragmentRabatu}</item></packet>`,
      czekajMs: rabat ? CZEKAJ_PO_POZYCJI_Z_RABATEM : CZEKAJ_PO_POZYCJI,
      czytaj: true,
    });
  }

  // Krok 2.5: kaucje jako <container type="out">.
  for (const kau of kontrakt.kaucje) {
    kroki.push({
      krok: 'container',
      lp: kau.lp,
      xml: `<packet><container action="${kau.akcja}" price="${kau.cena}" type="${kau.kierunek}" quantity="${kau.ilosc}"></container></packet>`,
      czekajMs: CZEKAJ_PO_KAUCJI,
      czytaj: true,
    });
  }

  // Krok 3: platnosci. Kwoty juz zawieraja kaucje - policzyl je serwer.
  for (const zaplata of kontrakt.platnosci) {
    kroki.push({
      krok: 'payment',
      lp: zaplata.lp,
      xml: `<packet><payment type="${zaplata.typ}" action="add" value="${zaplata.kwota}" name="${zaplata.nazwa}"></payment></packet>`,
      czekajMs: CZEKAJ_PO_PLATNOSCI,
      czytaj: true,
    });
  }

  // Krok 4: zamkniecie. total = suma FISKALNA (BEZ kaucji) - tak wymaga Novitus.
  // Atrybut nip dodajemy TYLKO gdy niepusty (pusty ciag = nie dodawaj atrybutu).
  let xmlClose = `<packet><receipt action="close" systemno="${naglowek.numer_systemowy}" checkout="${naglowek.kasa}" cashier="${naglowek.kasjer}" total="${kontrakt.sumy.suma_fiskalna}"`;
  if (naglowek.nip_nabywcy) {
    xmlClose += ` nip="${naglowek.nip_nabywcy}"`;
  }
  xmlClose += `/></packet>`;

  kroki.push({
    krok: 'close',
    xml: xmlClose,
    czekajMs: 0,
    czytaj: true,
    opcje: OPCJE_CLOSE,
    // Wyjatku z close NIE wypuszczamy do outer-catch: idzie do weryfikacji,
    // ktora rozstrzygnie no/unknown.
    przekazBlad: true,
  });

  return kroki;
}

// =============================================================================
// OSTRZEZENIA SERWERA - waga i REAKCJA przy kasie
// =============================================================================
/**
 * Serwer liczy wage kazdego ostrzezenia (WAGA_OSTRZEZEN w fiscal_receipt_builder.py),
 * ale przegladarka wrzucala dotad wszystko do jednego dymka na 15 sekund - takze
 * ostrzezenia oznaczone jako "krytyczne". Dymek przy kasie w godzinach szczytu
 * znaczy: nikt tego nie przeczyta. To ten sam mechanizm, przez ktory 35 pozycji
 * poszlo w 23% zamiast 5/8% i nikt tego nie zauwazyl.
 *
 * Tutaj kazdy kod dostaje REAKCJE, a nie tylko etykiete:
 *
 *   blokada       - do drukarki nie idzie NIC. Papier nie zgadzalby sie z kwota,
 *                   ktora klient zaplacil, albo z towarem, ktory wychodzi ze sklepu.
 *                   Paragonu fiskalnego nie da sie cofnac (tylko protokol pomylek),
 *                   a niewydrukowanie go da sie naprawic w minute: poprawiamy
 *                   transakcje i drukujemy ponownie z listy. Dlatego odmowa druku
 *                   jest tu TANSZA od zlego papieru.
 *   potwierdzenie - druk rusza dopiero, gdy kasjerka swiadomie klika OK w oknie,
 *                   ktore zatrzymuje caly ekran. Kwota do zaplaty jest dobra,
 *                   zly jest OPIS paragonu (stawka VAT, nazwa towaru, podzial
 *                   platnosci). Brak paragonu bylby tu gorszy od paragonu
 *                   z bledna stawka, wiec nie blokujemy - ale nie pozwalamy tez
 *                   przegapic.
 *   informacja    - dymek. Papier jest dobry, zostaje slad dla serwisu.
 *
 * DWIE ZMIANY WZGLEDEM WAG Z SERWERA (celowe, opisane tutaj, bo serwer wysyla
 * wage tylko informacyjnie - decyzje o druku podejmuje ta kasa):
 *
 *   ILOSC_NIEPOPRAWNA  serwer: ostrzezenie  ->  kasa: BLOKADA.
 *       To jedyny kod, przy ktorym POZYCJA WYPADA Z PARAGONU. Towar wychodzi
 *       ze sklepu, a na paragonie go nie ma - to sprzedaz bez ewidencji, czyli
 *       cieszy najgrubszy rodzaj bledu, jaki ta sciezka moze wyprodukowac.
 *       Przy ilosci ujemnej suma paragonu rozjezdza sie dodatkowo z kwota
 *       zaplaty. "Ostrzezenie" bylo tu stanowczo za slabe.
 *   PLATNOSC_DOMKNIETA serwer: ostrzezenie  ->  kasa: POTWIERDZENIE.
 *       Suma paragonu jest dobra, ale podzial gotowka/karta/BLIK zostal
 *       przesuniety, zeby sie domknal. Kasjerka rozliczajaca szuflade na koniec
 *       zmiany musi o tym wiedziec ZANIM zamknie zmiane, a nie szukac roznicy
 *       po godzinie.
 *
 * Nieznany kod (nowszy serwer, starsza kasa) NIGDY nie blokuje druku - najwyzej
 * prosi o potwierdzenie, gdy serwer nadal mu wage 'krytyczne'. Zablokowanie kasy
 * przez kod, ktorego ta wersja nie rozumie, zatrzymaloby sklep.
 */

export const POZIOM_BLOKADA = 'blokada';
export const POZIOM_POTWIERDZENIE = 'potwierdzenie';
export const POZIOM_INFORMACJA = 'informacja';

const RANGA_POZIOMU = {
  [POZIOM_INFORMACJA]: 1,
  [POZIOM_POTWIERDZENIE]: 2,
  [POZIOM_BLOKADA]: 3,
};

// Ile pozycji wypisujemy w oknie. Reszta jako "+N kolejnych" - okno na 30
// pozycji nikt nie czyta, a przewijanie w window.confirm nie istnieje.
const MAKS_W_KOMUNIKACIE = 5;

/**
 * Kod ostrzezenia -> co sie stalo i co kasjerka ma teraz zrobic.
 * Tekst pisany do kasjerki: bez nazw pol, bez slowa "fallback", bez procentow
 * VAT tam, gdzie nie sa potrzebne do decyzji.
 */
export const REAKCJA_NA_OSTRZEZENIE = {
  ILOSC_NIEPOPRAWNA: {
    poziom: POZIOM_BLOKADA,
    co: 'ten towar NIE trafi na paragon - drukarka dostalaby paragon bez niego',
    rob: 'Nie wydawaj towaru bez paragonu. Wezwij kierownika i zglos administratorowi systemu: transakcja wymaga korekty danych (z kasy nie da sie jej poprawic), a po korekcie paragon drukuje sie ponownie z listy transakcji.',
  },
  RABAT_PRZYCIETY: {
    poziom: POZIOM_BLOKADA,
    co: 'rabat jest wiekszy niz wartosc tej pozycji, wiec na paragonie wyszlaby INNA kwota niz ta, ktora klient zaplacil',
    rob: 'Nie drukuj. Wezwij kierownika i zglos administratorowi systemu: rabat tej transakcji wymaga korekty danych (z kasy nie da sie go poprawic). Po korekcie paragon drukuje sie ponownie z listy transakcji.',
  },
  STAWKA_NIEZNANA: {
    poziom: POZIOM_POTWIERDZENIE,
    co: 'ten towar ma w kartotece stawke VAT, ktorej drukarka nie zna - pojdzie na paragon w stawce 23%',
    rob: 'Kwota do zaplaty sie nie zmienia. Jesli klient czeka - potwierdz i drukuj, a zaraz potem zglos kierownikowi, ze towar ma zla stawke VAT.',
  },
  STAWKA_BRAK: {
    poziom: POZIOM_POTWIERDZENIE,
    co: 'ten towar nie ma w kartotece stawki VAT - pojdzie na paragon w stawce 23%',
    rob: 'Kwota do zaplaty sie nie zmienia. Jesli klient czeka - potwierdz i drukuj, a zaraz potem zglos kierownikowi brak stawki VAT przy tym towarze.',
  },
  // Dwa kody ponizej dolozyl rownolegly zespol do budowniczego na serwerze
  // (STAWKA_ROZBIEZNA, NAZWA_Z_KARTOTEKI). Bez wpisu tutaj wpadlyby w regule
  // dla kodow nieznanych i kasjerka zobaczylaby ogolnik zamiast konkretu.
  STAWKA_ROZBIEZNA: {
    poziom: POZIOM_POTWIERDZENIE,
    co: 'stawka VAT zapisana przy sprzedazy rozni sie od tej, ktora towar ma teraz w kartotece - na paragon idzie ta ze sprzedazy',
    rob: 'Kwota do zaplaty sie nie zmienia. Zanim potwierdzisz, upewnij sie u kierownika, ktora stawka jest wlasciwa - jesli zla poszla na paragon, trzeba bedzie robic korekte.',
  },
  NAZWA_Z_KARTOTEKI: {
    poziom: POZIOM_POTWIERDZENIE,
    co: 'towar nie mial zapisanej nazwy, wiec kasa wziela ja z kartoteki - dla drukarki to NOWY towar',
    rob: 'Po wydruku sprawdz na paragonie, czy nazwa zgadza sie z towarem, ktory sprzedajesz. Jesli nie - zglos kierownikowi przed nastepna taka sprzedaza.',
  },
  NAZWA_AWARYJNA: {
    poziom: POZIOM_POTWIERDZENIE,
    co: 'ten towar nie ma nazwy - na paragonie wydrukuje sie samo slowo "Produkt"',
    rob: 'Kwota jest dobra. Potwierdz i drukuj, jesli klient czeka, i zglos kierownikowi, ze towar nie ma nazwy.',
  },
  FLAGA_WYKLUCZENIA: {
    poziom: POZIOM_POTWIERDZENIE,
    co: 'ten towar mial byc wylaczony z rabatow, a rabat i tak zostal na niego naliczony',
    rob: 'Sprawdz, czy kwota na ekranie zgadza sie z ta, ktora wzielas od klienta. Zgadza sie - potwierdz i drukuj. Nie zgadza sie - anuluj i wezwij kierownika.',
  },
  PLATNOSC_DOMKNIETA: {
    poziom: POZIOM_POTWIERDZENIE,
    co: 'podzial zaplaty na gotowke, karte i BLIK nie sumowal sie do kwoty paragonu - roznica zostala dopisana do ostatniej formy platnosci',
    rob: 'Suma paragonu jest dobra, ale w rozliczeniu zmiany moze sie nie zgadzac gotowka z karta. Potwierdz, drukuj i zapisz to kierownikowi przy zamykaniu zmiany.',
  },
  BRAK_PRODUKT_ID: {
    poziom: POZIOM_INFORMACJA,
    co: 'nazwa na paragonie dostala zapasowy przedrostek, bo brakuje numeru towaru',
    rob: 'Drukuj normalnie. Zglos to przy okazji serwisowi.',
  },
  ZNAKI_OCZYSZCZONE: {
    poziom: POZIOM_INFORMACJA,
    co: 'z nazwy towaru zniknely znaki, ktorych drukarka nie umie wydrukowac',
    rob: 'Drukuj normalnie. Nazwa na paragonie bedzie krotsza niz na ekranie.',
  },
  LOG_NIEDOSTEPNY: {
    poziom: POZIOM_INFORMACJA,
    co: 'serwer nie zapisal sladu tego wydruku',
    rob: 'Drukuj normalnie, a potem sprawdz na liscie transakcji, czy paragon ma status zafiskalizowany.',
  },
};

/**
 * Zamienia jeden wpis z kontrakt.ostrzezenia na opis gotowy do pokazania.
 * Kody nieznane tej wersji kasy biora poziom z wagi serwera - ale nigdy blokady.
 */
export function opiszOstrzezenie(ostrzezenie) {
  const kod = ostrzezenie && ostrzezenie.kod ? String(ostrzezenie.kod) : 'NIEZNANE';
  const lp = ostrzezenie && ostrzezenie.lp != null ? ostrzezenie.lp : null;
  const znane = REAKCJA_NA_OSTRZEZENIE[kod];
  if (znane) {
    return { kod, lp, poziom: znane.poziom, co: znane.co, rob: znane.rob, znane: true };
  }
  const krytyczne = ostrzezenie && ostrzezenie.waga === 'krytyczne';
  return {
    kod,
    lp,
    poziom: krytyczne ? POZIOM_POTWIERDZENIE : POZIOM_INFORMACJA,
    co: krytyczne
      ? 'serwer zglosil powazna uwage do tego paragonu, ktorej ta kasa jeszcze nie zna'
      : 'serwer zglosil uwage do tego paragonu, ktorej ta kasa jeszcze nie zna',
    rob: krytyczne
      ? 'Sprawdz kwote na ekranie i na paragonie. Zgadza sie - drukuj i zglos kierownikowi. Nie zgadza sie - anuluj.'
      : 'Drukuj normalnie i zglos to serwisowi.',
    znane: false,
  };
}

function wierszOpisu(opis, numer) {
  const gdzie = opis.lp != null ? `Pozycja ${opis.lp}: ` : '';
  return `${numer}) ${gdzie}${opis.co}.\n   Co robisz: ${opis.rob}`;
}

function listaWierszy(opisy) {
  const widoczne = opisy.slice(0, MAKS_W_KOMUNIKACIE);
  const wiersze = widoczne.map((o, i) => wierszOpisu(o, i + 1));
  if (opisy.length > widoczne.length) {
    wiersze.push(`... i ${opisy.length - widoczne.length} kolejnych uwag tego samego rodzaju.`);
  }
  return wiersze.join('\n\n');
}

function kodyDlaSerwisu(opisy) {
  const unikalne = [];
  for (const o of opisy) {
    if (!unikalne.includes(o.kod)) unikalne.push(o.kod);
  }
  return unikalne.join(', ');
}

/**
 * Dzieli ostrzezenia z kontraktu na trzy kubelki i sklada gotowe komunikaty.
 *
 * @param {Array} ostrzezenia - kontrakt.ostrzezenia (moze byc puste/undefined)
 * @returns {{
 *   poziom: string, wszystkie: Array, blokujace: Array, doPotwierdzenia: Array,
 *   informacyjne: Array, tekstBlokady: (string|null),
 *   tekstPotwierdzenia: (string|null), tekstInformacji: (string|null), kody: string
 * }}
 */
export function ocenOstrzezenia(ostrzezenia) {
  const lista = Array.isArray(ostrzezenia) ? ostrzezenia : [];
  const wszystkie = lista
    .filter(o => o && typeof o === 'object')
    .map(opiszOstrzezenie);

  const blokujace = wszystkie.filter(o => o.poziom === POZIOM_BLOKADA);
  const doPotwierdzenia = wszystkie.filter(o => o.poziom === POZIOM_POTWIERDZENIE);
  const informacyjne = wszystkie.filter(o => o.poziom === POZIOM_INFORMACJA);

  let poziom = POZIOM_INFORMACJA;
  for (const o of wszystkie) {
    if (RANGA_POZIOMU[o.poziom] > RANGA_POZIOMU[poziom]) poziom = o.poziom;
  }

  const tekstBlokady = blokujace.length === 0 ? null : [
    'PARAGON NIE ZOSTAL WYDRUKOWANY',
    '',
    'Dane tego paragonu sa niepoprawne - na papierze wyszedlby inny towar albo inna kwota niz ta, ktora klient zaplacil. Paragonu fiskalnego nie da sie cofnac, dlatego kasa go nie wydrukowala.',
    '',
    listaWierszy(blokujace),
    '',
    `Kody dla serwisu: ${kodyDlaSerwisu(blokujace)}`,
  ].join('\n');

  const tekstPotwierdzenia = doPotwierdzenia.length === 0 ? null : [
    'UWAGA - PARAGON WYJDZIE Z BLEDEM',
    '',
    'Kwota do zaplaty jest dobra, ale to, co bedzie na papierze, nie zgadza sie z kartoteka:',
    '',
    listaWierszy(doPotwierdzenia),
    '',
    'OK  = drukuje paragon mimo tej uwagi.',
    'Anuluj = paragon NIE zostanie wydrukowany.',
    '',
    `Kody dla serwisu: ${kodyDlaSerwisu(doPotwierdzenia)}`,
  ].join('\n');

  const tekstInformacji = informacyjne.length === 0 ? null : (() => {
    const widoczne = informacyjne.slice(0, 3);
    const opis = widoczne
      .map(o => `${o.lp != null ? `poz. ${o.lp} - ` : ''}${o.co} [${o.kod}]`)
      .join('; ');
    const reszta = informacyjne.length > widoczne.length
      ? ` (+${informacyjne.length - widoczne.length} wiecej)`
      : '';
    return `Uwagi do paragonu: ${opis}${reszta}`;
  })();

  return {
    poziom,
    wszystkie,
    blokujace,
    doPotwierdzenia,
    informacyjne,
    tekstBlokady,
    tekstPotwierdzenia,
    tekstInformacji,
    kody: kodyDlaSerwisu(wszystkie),
  };
}
